export const content = {
  en: {
    meta: {
      title: "Mezzbridge – Mezzanine capital for established companies",
      description: "Mezzbridge introduces established companies to a licensed finance broker for mezzanine capital – silent participations and subordinated loans from family offices, funds and investment companies.",
    },
    nav: { how: "How it works", need: "What we need", faq: "FAQ", cta: "Request a call" },
    hero: {
      eyebrow: "Mezzanine capital for established companies",
      title: "Growth capital beyond the classic bank loan",
      sub: "We introduce companies to a licensed finance broker who arranges mezzanine capital from family offices, funds and investment companies – as a silent participation or subordinated loan.",
      cta1: "Request an initial call",
      cta2: "How it works",
    },
    trust: [
      { t: "Licensed broker partner", d: "Licensed under § 34c GewO" },
      { t: "Two instruments", d: "Silent participation or subordinated loan" },
      { t: "Institutional capital", d: "Family offices, funds, investment companies" },
      { t: "Individual review", d: "Every case is assessed on its own merits" },
    ],
    why: {
      eyebrow: "Why Mezzbridge",
      h2: "Access to capital beyond your house bank",
      lead: "Through our licensed broker partner, your application reaches a broad network of institutional capital providers – not just one lender.",
      cards: [
        { t: "European and South African family offices", d: "Capital providers in Europe and in South Africa, matched to your case." },
        { t: "Financing in USD as well", d: "Not limited to one currency: structures in USD are possible." },
        { t: "No upper ticket limit", d: "From about ZAR 20 million upwards, with no fixed maximum." },
      ],
    },
    how: {
      eyebrow: "How it works",
      h2: "Four steps to a complete application",
      steps: [
        { t: "Initial call", d: "A short call about your company, your capital requirement and the process." },
        { t: "Forms & documents", d: "You complete the enquiry forms and send the documents listed below as PDF." },
        { t: "Hand-over to the broker", d: "We hand your documents to our broker partner." },
        { t: "Mandate & agreement", d: "The broker prepares the capital procurement agreement." },
      ],
    },
    need: {
      eyebrow: "What we need",
      h2: "Documents for your application",
      lead: "No need to prepare everything upfront – we'll go through the details together on the call.",
      toggleShow: "Show full document list",
      toggleHide: "Hide full document list",
      cards: [
        { t: "Formalities", items: ["Signed data protection declaration", "ID copy of all authorised signatories", "Company register extract, max. 3 months old", "VAT identification number"] },
        { t: "Financials", items: ["Annual financial statements, last 2–3 years", "Current management accounts incl. trial balance", "Order book or reliable revenue plan", "Overview of existing liabilities"] },
        { t: "Project", items: ["Business plan with Plan P&L and liquidity plan", "Use-of-funds plan with drawdown tranches", "Signed enquiry forms", "Mezzanine capital loan application"] },
      ],
    },
    sense: {
      eyebrow: "Is it right for you?",
      h2: "When does mezzanine capital make sense?",
      lead: "Mezzanine capital ranks behind your bank debt but ahead of equity. It funds growth without selling shares or giving up control.",
      noteT: "Please note",
      note: "Mezzanine capital is not a classic, low-cost bank loan – it ranks behind bank debt and is priced accordingly. Every financing is reviewed individually; there is no guaranteed financing or payout.",
      fitsT: "A good fit",
      fits: [
        "Expansion: new sites, capacity or markets where the bank covers only part",
        "Acquisition: buying a competitor, supplier or complementary business",
        "Succession: the next generation or management buys out the founder",
        "Shareholder buyout: one partner exits, the others take over",
        "Ownership transactions, including BEE deals",
      ],
      notT: "Not the right fit",
      nots: [
        "Start-ups and early-stage companies – venture capital or angel investors are the better route",
        "Loss-making businesses or restructurings",
        "The cheapest possible loan: if the bank funds the full amount, it is usually cheaper",
        "Amounts below about ZAR 20 million",
        "Property development and single projects without an operating business",
      ],
      lookT: "What capital providers typically look for",
      look: [
        "Audited annual accounts for the last 2–3 years",
        "A plausible, profitable business plan over five years with a specific use of funds – what the capital is for and what it will earn",
        "Steady or growing EBITDA and the ability to pay interest from operating cash flow",
        "Moderate existing debt, leaving room for an additional loan",
        "Income in EUR, GBP or USD, e.g. from exports or foreign clients",
        "A transparent shareholder and financing structure",
        "An experienced management team beyond the owner",
      ],
      checkT: "Quick self-check",
      checkItems: [
        "Operating for 3 years or more",
        "Profitable for at least the last 2 years",
        "Funding need of ZAR 20 million or more",
        "A concrete step: expansion, acquisition, succession or buyout",
        "I want to keep control of my business",
      ],
      checkMsg: "{n} of 5 ticks – from 4 on, a first conversation is worthwhile.",
      checkOk: "Looks like a good fit – let's talk.",
      ctaLabel: "Book your 30-minute call",
    },
    costs: {
      eyebrow: "Costs",
      h2: "Transparent from the first call",
      items: [
        { v: "6–12 %", d: "Interest p.a. to plan with in your business plan. Actual terms are set by the capital provider." },
        { v: "10 %", d: "Success-based commission of the broker on the capital raised." },
        { v: "Deposit", d: "Agreed only in the licensed broker's brokerage agreement – never before or during the initial call. Credited in full against the commission on success, refunded in full if there is no success.*" },
      ],
      footnote: "* As stated by the broker. The amount of the deposit and all binding terms are set exclusively in the broker's brokerage agreement.",
    },
    role: {
      eyebrow: "Our role",
      h2: "We introduce – the licensed broker arranges",
      p1: "Mezzbridge is an introducer service of 2besales GmbH. We make the first contact, collect your documents and pass them to our broker partner.",
      p2: "We do not provide investment, financial, legal or tax advice, and we do not broker financing ourselves.",
      name: "Patrick Häfner",
      role1: "Your first point of contact · Based in Cape Town",
      role2: "Managing Director, 2besales GmbH",
      linkedin: "https://www.linkedin.com/in/patrickh%C3%A4fner/",
      linkedinLabel: "Connect on LinkedIn",
      chain: [
        { t: "You", d: "Business plan, documents, decisions" },
        { t: "Mezzbridge", d: "First contact" },
        { t: "Licensed broker", d: "Structuring and placement" },
        { t: "Capital providers", d: "Due diligence and decision" },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      h2: "Frequently asked questions",
      items: [
        { q: "Is financing guaranteed?", a: "No. Every financing is subject to an individual review. The decision rests solely with the capital providers." },
        { q: "What does the introduction cost me?", a: "Our introduction is free of charge for you. We receive a fee from the broker partner if a financing is concluded. The broker's own commission is shown above." },
        { q: "What amounts and terms are possible?", a: "Amounts from ZAR 20 million; terms of up to 10 years. The actual terms depend on the review of your case." },
        { q: "How long does the process take?", a: "From review to payout it takes on average 3 months, depending on the case." },
        { q: "Is my data treated confidentially?", a: "Yes. Your information is used only to handle your enquiry and is passed on to the broker partner only with your consent." },
        { q: "When does mezzanine capital make sense?", a: "When a business needs more capital than the bank will lend and the owner does not want new shareholders – for example expansion, acquisition, succession or a shareholder buyout." },
        { q: "Who is it not right for?", a: "Start-ups and early-stage companies, loss-making businesses or restructurings, amounts below about ZAR 20 million, and property development or single projects without an operating business." },
        { q: "What capital providers typically look for?", a: "Audited financial statements for the last 3 years, steady or growing EBITDA, moderate existing debt and a specific use of funds with numbers." },
        { q: "Do I lose control of my company?", a: "No. Mezzanine capital is a loan – you do not sell shares and you keep full control of your business." },
        { q: "Is only mezzanine capital possible?", a: "Mezzanine capital – as a silent participation or subordinated loan – is our focus. Where it fits the case, our broker partner can also arrange equity capital. This is clarified individually in the initial call." },
      ],
    },
    booking: {
      eyebrow: "Get in touch",
      h2: "Request an initial call",
      lead: "Book a 30-minute call directly in the calendar – pick a time that suits you.",
      note: "No obligation. No guarantee of financing.",
      cardT: "Book a call directly",
      cardP: "Pick a time that suits you – the booking calendar opens right here.",
      notePre: "When the booking calendar loads, data is transferred to Calendly (Calendly LLC, USA). See our ",
      noteLink: "privacy policy",
      notePost: ".",
      openBtn: "Open in Calendly",
    },
    footer: {
      service: " is a service of 2besales GmbH",
      line1: "Lena-Christ-Str. 2, 82031 Grünwald, Germany",
      line2: "Managing Director: Patrick Häfner · Register court: Amtsgericht München, HRB 293569 · VAT ID: DE368739520",
      links: [
        { label: "Imprint", to: "/imprint" },
        { label: "Privacy policy", to: "/privacy" },
      ],
      disclaimer: "Mezzbridge acts solely as an introducer and provides no investment, financial, legal or tax advice. Brokerage is carried out exclusively by a broker licensed under § 34c GewO. Mezzanine capital involves risk; every financing is subject to individual review and there is no guarantee of financing or payout.",
    },
  },
  de: {
    meta: {
      title: "Mezzbridge – Mezzanine-Kapital für etablierte Unternehmen",
      description: "Mezzbridge stellt den Kontakt zwischen etablierten Unternehmen und einem lizenzierten deutschen Finanzmakler für Mezzanine-Kapital her – stille Beteiligungen und Nachrangdarlehen von Family Offices, Fonds und Beteiligungsgesellschaften.",
    },
    nav: { how: "So funktioniert es", need: "Unterlagen", faq: "FAQ", cta: "Gespräch anfragen" },
    hero: {
      eyebrow: "Mezzanine-Kapital für etablierte Unternehmen",
      title: "Wachstumskapital jenseits des klassischen Bankkredits",
      sub: "Wir stellen den Kontakt zu einem lizenzierten deutschen Finanzmakler her, der Mezzanine-Kapital von Family Offices, Fonds und Beteiligungsgesellschaften vermittelt – als stille Beteiligung oder Nachrangdarlehen.",
      cta1: "Erstgespräch anfragen",
      cta2: "So funktioniert es",
    },
    trust: [
      { t: "Lizenzierter Maklerpartner", d: "Erlaubnis nach § 34c GewO" },
      { t: "Zwei Instrumente", d: "Stille Beteiligung oder Nachrangdarlehen" },
      { t: "Institutionelles Kapital", d: "Family Offices, Fonds, Beteiligungsgesellschaften" },
      { t: "Individuelle Prüfung", d: "Jeder Fall wird einzeln bewertet" },
    ],
    why: {
      eyebrow: "Warum Mezzbridge",
      h2: "Kapitalzugang über Ihre Hausbank hinaus",
      lead: "Über unseren lizenzierten Maklerpartner gelangt Ihre Anfrage in ein breites Netzwerk institutioneller Kapitalgeber – nicht nur an einen Geldgeber.",
      cards: [
        { t: "Europäische Family Offices", d: "Kapitalgeber in Europa, passend zu Ihrem Fall." },
        { t: "Auch Finanzierung in USD", d: "Nicht auf eine Währung beschränkt: Strukturen in USD sind möglich." },
        { t: "Kein Ticket-Limit nach oben", d: "Ab rund 1 Mio. Euro aufwärts, ohne feste Obergrenze." },
      ],
    },
    how: {
      eyebrow: "So funktioniert es",
      h2: "In vier Schritten zur vollständigen Anfrage",
      steps: [
        { t: "Erstgespräch", d: "Ein kurzes Gespräch über Ihr Unternehmen, Ihren Kapitalbedarf und den Ablauf." },
        { t: "Formulare & Unterlagen", d: "Sie füllen die Anfrageformulare aus und senden die unten genannten Unterlagen als PDF." },
        { t: "Übergabe an den Makler", d: "Wir übergeben Ihre Unterlagen an unseren Maklerpartner." },
        { t: "Mandat & Vertrag", d: "Der Makler erstellt den Kapitalbesorgungsvertrag." },
      ],
    },
    need: {
      eyebrow: "Unterlagen",
      h2: "Was wir für Ihre Anfrage benötigen",
      lead: "Sie müssen nicht alles vorab vorbereiten – die Details besprechen wir gemeinsam im Gespräch.",
      toggleShow: "Vollständige Unterlagenliste anzeigen",
      toggleHide: "Vollständige Unterlagenliste ausblenden",
      cards: [
        { t: "Formalien", items: ["Unterschriebene Datenschutzerklärung", "Ausweiskopie aller Zeichnungsberechtigten", "Handelsregisterauszug, max. 3 Monate alt", "Umsatzsteuer-Identifikationsnummer"] },
        { t: "Finanzen", items: ["Jahresabschlüsse der letzten 2–3 Jahre", "Aktuelle BWA inkl. Summen- und Saldenliste", "Auftragsbestand oder belastbare Umsatzplanung", "Übersicht bestehender Verbindlichkeiten"] },
        { t: "Vorhaben", items: ["Businessplan mit Plan-GuV und Liquiditätsplanung", "Mittelverwendungsplan mit Abruf-Tranchen", "Unterschriebene Anfrageformulare", "Mezzanine-Kapital-Kreditantrag"] },
      ],
    },
    sense: {
      eyebrow: "Passt es zu Ihnen?",
      h2: "Wann macht Mezzanine Sinn?",
      lead: "Mezzanine-Kapital ist nachrangig zu Ihrer Bankfinanzierung, aber vorrangig zum Eigenkapital. Es finanziert Wachstum, ohne Anteile zu verkaufen oder Kontrolle abzugeben.",
      noteT: "Bitte beachten",
      note: "Mezzanine-Kapital ist kein klassischer, günstiger Bankkredit – es ist nachrangig und entsprechend bepreist. Jede Finanzierung unterliegt einer individuellen Prüfung; eine Finanzierung oder Auszahlung ist nicht garantiert.",
      fitsT: "Wofür es passt",
      fits: [
        "Expansion: neue Standorte, Kapazitäten oder Märkte, wenn die Bank nur einen Teil finanziert",
        "Akquisition: Kauf eines Wettbewerbers, Lieferanten oder ergänzenden Betriebs",
        "Nachfolge: die nächste Generation oder das Management übernimmt",
        "Gesellschafterausscheiden: ein Partner geht, die anderen übernehmen",
        "Gesellschaftertransaktionen",
      ],
      notT: "Nicht geeignet",
      nots: [
        "Start-ups und Frühphasen-Unternehmen – hier sind Risikokapital oder Business Angels der bessere Weg",
        "Verlustphasige Unternehmen oder Restrukturierungen",
        "Der günstigste Kredit: deckt die Bank alles ab, ist sie meist günstiger",
        "Beträge unter rund 1 Mio. Euro",
        "Immobilienentwicklungen und einzelne Projekte ohne operatives Geschäft",
      ],
      lookT: "Worauf Kapitalgeber typischerweise achten",
      look: [
        "Geprüfte Jahresabschlüsse der letzten 2–3 Jahre",
        "Ein plausibler, rentabler Businessplan über fünf Jahre mit konkretem Verwendungszweck – wofür das Kapital gedacht ist und was es einbringt",
        "Stabile oder wachsende EBITDA und Zinsen, die aus dem operativen Cashflow bedient werden können",
        "Moderate bestehende Verschuldung, die Spielraum für ein zusätzliches Darlehen lässt",
        "Einnahmen in EUR, GBP oder USD, z. B. aus Exporten oder ausländischen Kunden",
        "Eine transparente Gesellschafter- und Finanzierungsstruktur",
        "Ein erfahrenes Managementteam neben dem Inhaber",
      ],
      checkT: "Kurzer Selbstcheck",
      checkItems: [
        "Seit 3 Jahren oder länger am Markt",
        "Seit mindestens 2 Jahren profitabel",
        "Kapitalbedarf von 1 Mio. Euro oder mehr",
        "Ein konkreter Schritt: Expansion, Akquisition, Nachfolge oder Gesellschafterwechsel",
        "Ich möchte die Kontrolle über mein Unternehmen behalten",
      ],
      checkMsg: "{n} von 5 Punkten angekreuzt – ab 4 lohnt sich ein Erstgespräch.",
      checkOk: "Sieht nach einem guten Fall aus – lassen Sie uns sprechen.",
      ctaLabel: "30-minütiges Gespräch buchen",
    },
    costs: {
      eyebrow: "Kosten",
      h2: "Transparent ab dem ersten Gespräch",
      items: [
        { v: "6–12 %", d: "Zinsen p. a., mit denen Sie im Businessplan kalkulieren. Die tatsächlichen Konditionen legt der Kapitalgeber fest." },
        { v: "10 %", d: "Erfolgsabhängige Provision des Maklers auf das vermittelte Kapital." },
        { v: "Anzahlung", d: "Wird ausschließlich im Maklervertrag des lizenzierten Maklers vereinbart – nie vor oder während des Erstgesprächs. Im Erfolgsfall voll auf die Provision angerechnet, ohne Erfolg voll zurückerstattet.*" },
      ],
      footnote: "* Angaben des Maklers. Höhe der Anzahlung und alle verbindlichen Bedingungen regelt ausschließlich der Maklervertrag.",
    },
    role: {
      eyebrow: "Unsere Rolle",
      h2: "Wir stellen den Kontakt her – der lizenzierte Makler vermittelt",
      p1: "Mezzbridge ist ein Tippgeber-Service der 2besales GmbH. Wir stellen den ersten Kontakt her, sammeln Ihre Unterlagen und übergeben sie an unseren Maklerpartner.",
      p2: "Wir erbringen keine Anlage-, Finanz-, Rechts- oder Steuerberatung und vermitteln selbst keine Finanzierungen.",
      name: "Patrick Häfner",
      role1: "Ihr erster Ansprechpartner · Ansässig in Kapstadt",
      role2: "Geschäftsführer, 2besales GmbH",
      linkedin: "https://www.linkedin.com/in/patrickh%C3%A4fner/",
      linkedinLabel: "Auf LinkedIn vernetzen",
      chain: [
        { t: "Sie", d: "Businessplan, Unterlagen, Entscheidungen" },
        { t: "Mezzbridge", d: "Erstkontakt" },
        { t: "Lizenzierter Makler", d: "Strukturierung und Platzierung" },
        { t: "Kapitalgeber", d: "Due Diligence und Entscheidung" },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      h2: "Häufige Fragen",
      items: [
        { q: "Ist eine Finanzierung garantiert?", a: "Nein. Jede Finanzierung unterliegt einer individuellen Prüfung. Die Entscheidung liegt allein bei den Kapitalgebern." },
        { q: "Was kostet mich die Kontaktvermittlung?", a: "Für Sie ist unsere Kontaktherstellung kostenfrei. Wir erhalten bei erfolgreichem Abschluss eine Vergütung vom Maklerpartner. Die Provision des Maklers sehen Sie oben." },
        { q: "Welche Beträge und Laufzeiten sind möglich?", a: "Beträge ab 1 Mio. Euro; Laufzeiten bis zu 10 Jahre. Die konkreten Konditionen hängen von der Prüfung Ihres Falls ab." },
        { q: "Wie lange dauert der Prozess?", a: "Von der Prüfung bis zur Auszahlung dauert es im Durchschnitt 3 Monate, je nach Fall." },
        { q: "Werden meine Daten vertraulich behandelt?", a: "Ja. Ihre Angaben werden nur zur Bearbeitung Ihrer Anfrage verwendet und nur mit Ihrer Einwilligung an den Maklerpartner weitergegeben." },
        { q: "Wann macht Mezzanine Sinn?", a: "Wenn ein Unternehmen mehr Kapital benötigt, als die Bank leiht, und der Inhaber keine neuen Gesellschafter aufnehmen will – etwa bei Expansion, Akquisition, Nachfolge oder dem Ausscheiden eines Gesellschafters." },
        { q: "Für wen ist es nicht geeignet?", a: "Für Start-ups und Frühphasen-Unternehmen, verlustphasige Unternehmen oder Restrukturierungen, Beträge unter rund 1 Mio. Euro sowie Immobilienentwicklungen oder einzelne Projekte ohne operatives Geschäft." },
        { q: "Worauf Kapitalgeber typischerweise achten?", a: "Geprüfte Jahresabschlüsse der letzten 3 Jahre, stabile oder wachsende EBITDA, moderate bestehende Verschuldung und ein konkreter Verwendungszweck mit Zahlen." },
        { q: "Verliere ich die Kontrolle über mein Unternehmen?", a: "Nein. Mezzanine-Kapital ist ein Darlehen – Sie verkaufen keine Anteile und behalten die volle Kontrolle." },
        { q: "Ist nur Mezzanine-Kapital möglich?", a: "Mezzanine-Kapital – als stille Beteiligung oder Nachrangdarlehen – ist unser Fokus. Wo es zum Fall passt, kann unser Maklerpartner auch Eigenkapital vermitteln. Das wird individuell im Erstgespräch geklärt." },
      ],
    },
    booking: {
      eyebrow: "Kontakt",
      h2: "Erstgespräch anfragen",
      lead: "Buchen Sie direkt im Kalender ein 30-minütiges Gespräch – zu einer Zeit, die Ihnen passt.",
      note: "Unverbindlich. Keine Finanzierungszusage.",
      cardT: "Termin direkt buchen",
      cardP: "Wählen Sie einen passenden Termin – der Buchungskalender öffnet sich direkt hier.",
      notePre: "Beim Laden des Buchungskalenders werden Daten an Calendly (Calendly LLC, USA) übertragen. Details in unserer ",
      noteLink: "Datenschutzerklärung",
      notePost: ".",
      openBtn: "In Calendly öffnen",
    },
    footer: {
      service: " ist ein Service der 2besales GmbH",
      line1: "Lena-Christ-Str. 2, 82031 Grünwald",
      line2: "Geschäftsführer: Patrick Häfner · Amtsgericht München, HRB 293569 · USt-IdNr.: DE368739520",
      links: [
        { label: "Impressum", to: "/imprint" },
        { label: "Datenschutz", to: "/privacy" },
      ],
      disclaimer: "Mezzbridge ist ausschließlich als Tippgeber tätig und erbringt keine Anlage-, Finanz-, Rechts- oder Steuerberatung. Die Vermittlung erfolgt ausschließlich durch einen Makler mit Erlaubnis nach § 34c GewO. Mezzanine-Kapital ist mit Risiken verbunden; jede Finanzierung unterliegt einer individuellen Prüfung, eine Finanzierung oder Auszahlung ist nicht garantiert.",
    },
  },
};
